// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AffiliateCouponCode, CreateCouponRequest, CouponValidationResult } from '@/types/affiliateCoupons';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

export const useAffiliateCoupons = (affiliateId?: string) => {
  const [coupons, setCoupons] = useState<AffiliateCouponCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('affiliate_coupon_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (affiliateId) {
        query = query.eq('affiliate_id', affiliateId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setCoupons(data || []);
    } catch (err) {
      console.error('Error fetching affiliate coupons:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [affiliateId]);

  const createCoupon = async (couponData: CreateCouponRequest): Promise<boolean> => {
    try {
      const { error: insertError } = await supabase
        .from('affiliate_coupon_codes')
        .insert(couponData);

      if (insertError) throw insertError;

      toast.success('Coupon code created successfully');
      await fetchCoupons(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error creating coupon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create coupon');
      return false;
    }
  };

  const updateCoupon = async (couponId: string, updates: Partial<AffiliateCouponCode>): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('affiliate_coupon_codes')
        .update(updates)
        .eq('id', couponId);

      if (updateError) throw updateError;

      toast.success('Coupon code updated successfully');
      await fetchCoupons(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error updating coupon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update coupon');
      return false;
    }
  };

  const deleteCoupon = async (couponId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('affiliate_coupon_codes')
        .delete()
        .eq('id', couponId);

      if (deleteError) throw deleteError;

      toast.success('Coupon code deleted successfully');
      await fetchCoupons(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error deleting coupon:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete coupon');
      return false;
    }
  };

  const validateCoupon = async (code: string, purchaseType: string = 'membership'): Promise<CouponValidationResult> => {
    try {
      const { data: { user } } = await getSessionUser();
      
      const { data, error } = await invokeEdgeFunction('validate-affiliate-coupon-code', {
        body: { 
          coupon_code: code.toUpperCase(),
          purchase_type: purchaseType,
          user_id: user?.id
        }
      });

      if (error) throw error;

      return data as CouponValidationResult;
    } catch (err) {
      console.error('Error validating coupon:', err);
      return {
        valid: false,
        error: err instanceof Error ? err.message : 'Failed to validate coupon'
      };
    }
  };

  const incrementUsage = async (couponId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('increment_coupon_usage', {
        coupon_id: couponId
      });

      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Error incrementing coupon usage:', err);
      return false;
    }
  };

  return {
    coupons,
    loading,
    error,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    validateCoupon,
    incrementUsage,
    refetch: fetchCoupons
  };
};